import { appContext } from "../../../../AppContext";
import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IRouterProps, withRouting } from "@iconics/components";
import { Ontology, VirtualTreeView } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { ApiConstants } from "../../../../Providers.Api/ApiConstants";
import { SerendieSymbol } from "@serendie/symbols";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { MultilingualTextField } from "@iconics/components";
import { Switch } from "@serendie/ui";
import { Validator } from '@iconics/core';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, TextArea, TextField } from "@iconics/components";

class EditVirtualTreeView extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private labels = appContext().labels;
    private get isNew() { return !this.props.params.id; }
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state = {
            isEnabled: false,
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            descriptionError: '',
            definition: '',
            definitionError: '',
            ontologies: [],
            ontologyId: '',
            ontologyName: '',
            ontologyIdError: '',
            eTag: '',
            canDelete: appContext().localStorageProvider.hasRight('DATAMODEL.VirtualTreeView.Delete'),
            sections: [],
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_VirtualTreeView_PageTitle;
        this.backUrl = `/admin/virtual-tree-views`;
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();
        this.validator
            .addInput('ontologyId', () => this.state.ontologyId, i => this.setState({ ontologyIdError: i }))
            .isRequired();
        this.validator
            .addInput('definition', () => this.state.definition, i => this.setState({ definitionError: i }))
            .isRequired();

        await this.loadOntologies();

        if (!this.isNew)
        {
            await this.loading(async () =>
            {
                const virtualTreeView = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.byId.get<VirtualTreeView>({
                    id: this.props.params.id ?? '',
                    select: VirtualTreeView,
                    tenancy: appContext().tenantId,
                });

                this.setState({
                    isEnabled: virtualTreeView.IsEnabled,
                    name: virtualTreeView.Name,
                    displayName: this.languageValuesHelper.fromString(virtualTreeView.DisplayName),
                    description: virtualTreeView.Description,
                    definition: virtualTreeView.Definition,
                    ontologyId: virtualTreeView.OntologyId,
                    ontologyName: this.state.ontologies.find(x => x.Id === virtualTreeView.OntologyId)?.Name ?? '',
                    eTag: virtualTreeView['@odata.etag'],
                });
            });
        }
    }

    private async loadOntologies(): Promise<void>
    {
        try
        {
            const ontologies = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.get<PagedResponse<Ontology[]>>({
                select: Ontology,
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });
            this.setState({ ontologies: ontologies.value });
        }
        catch (error)
        {
            console.error(error)
        }
    }
    
    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.byId.delete({
                id: this.props.params.id ?? '',
                tenancy: appContext().tenantId,
            });
            this.props.navigate('/admin/virtual-tree-views');
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (!this.isNew)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.byId.patch({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Description: this.state.description,
                        Definition: this.state.definition,
                        OntologyId: this.state.ontologyId,
                        '@odata.etag': this.state.eTag,
                    },
                    id: Number.parseInt(this.props.params.id ?? ''),
                    tenancy: appContext().tenantId,
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.post({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Description: this.state.description,
                        Definition: this.state.definition,
                        OntologyId: this.state.ontologyId,
                    },
                    tenancy: appContext().tenantId,
                });
            }
            this.props.navigate('/admin/virtual-tree-views');
        });
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <Card
                        title={this.labels.Platform_VirtualTreeView_DetailsTitle}
                        subTitle={this.labels.Platform_VirtualTreeView_DetailsSubTitle}
                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                        icon={<SerendieSymbol name='layout-row' />}
                    >
                        <GridContainer>
                            <GridItem>
                                <Switch
                                    label={this.labels.VirtualTreeView_IsEnabled}
                                    checked={this.state.isEnabled}
                                    onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <TextField
                                    required
                                    label={this.labels.VirtualTreeView_Name}
                                    placeholder={this.labels.VirtualTreeView_Name}
                                    value={this.state.name}
                                    onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                    invalid={!!this.state.nameError}
                                    invalidMessage={this.state.nameError}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <MultilingualTextField
                                    label={this.labels.VirtualTreeView_DisplayName}
                                    placeholder={this.labels.VirtualTreeView_DisplayName}
                                    value={this.state.displayName}
                                    onChange={value => this.setState({ displayName: value })}
                                    localisation={appContext().localisation}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <TextField
                                    required
                                    label={this.labels.VirtualTreeView_Description}
                                    placeholder={this.labels.VirtualTreeView_Description}
                                    value={this.state.description}
                                    onChange={e => this.setState({ description: e.target.value }, () => this.validator.validate('description'))}
                                    invalid={!!this.state.descriptionError}
                                    invalidMessage={this.state.descriptionError}
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <Select
                                    label={this.labels.VirtualTreeView_OntologyId}
                                    placeholder={this.state.ontologyName}
                                    value={[this.state.ontologyId]}
                                    onSelect={e => this.setState({ ontologyId: e.value }, () => this.validator.validate('ontologyId'))}
                                    items={this.state.ontologies.filter(x => x.IsEnabled === 'Yes').map(x => ({ label: x.Name, value: x.Id }))}
                                    required
                                    invalid={!!this.state.ontologyIdError}
                                    invalidMessage={this.state.ontologyIdError}
                                />
                            </GridItem>
                            <GridItem>
                                <TextArea
                                    required
                                    label={this.labels.VirtualTreeView_Definition}
                                    placeholder={this.labels.VirtualTreeView_Definition}
                                    value={this.state.definition}
                                    onChange={e => this.setState({ definition: e.target.value }, () => this.validator.validate('definition'))}
                                    rows={15}
                                    invalid={!!this.state.definitionError}
                                    invalidMessage={this.state.definitionError}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.isNew &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={this.labels.Platform_VirtualTreeView_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate('/admin/virtual-tree-views')}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }
}

export default withRouting(EditVirtualTreeView);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
}

export interface IState
{
    isEnabled: boolean;
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    description: string;
    descriptionError: string;
    definition: string;
    definitionError: string;
    ontologies: Ontology[];
    ontologyId: string;
    ontologyName: string;
    ontologyIdError: string;
    eTag: string;
    canDelete: boolean;
    sections: IPageNavSection[];
}
